import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFlavour } from '../../context/FlavourContext';
import { useCart } from '../../context/CartContext';
import { useWoo } from '../../context/WooContext';
import { BUNDLES, FLAVOUR_ORDER, FLAVOURS } from '../../data/mock';
import Reveal from '../../components/Reveal';
import ProductBox from '../../components/ProductBox';
import { useLang } from '../../context/LanguageContext';

export function Purchase() {
  const { flavour } = useFlavour();
  const { addItem } = useCart();
  const { priceOf, money } = useWoo();

  const available = flavour.available !== false && !!flavour.woo;
  const unit = available ? priceOf(flavour.woo) : null;
  const availIds = FLAVOUR_ORDER.filter((id) => FLAVOURS[id].available !== false && FLAVOURS[id].woo);
  const mixSum = availIds.reduce((s, id) => s + (priceOf(FLAVOURS[id].woo) || 0), 0);

  const buy = (b) => {
    if (b.type === 'mix') {
      const wooItems = availIds.map((id) => ({ id: FLAVOURS[id].woo, quantity: 1 }));
      addItem({ key: 'mix-preset', title: `S-MAN MIX — ${availIds.length} FLAVOURS`, flavour: 'mix', price: mixSum, qty: 1, boxes: availIds.length, type: 'mix', color: '#ff2d2d', rgb: '255,45,45', wooItems });
    } else {
      if (!available) return;
      addItem({ key: `bundle-${b.id}-${flavour.id}`, title: `S-MAN ${flavour.name} — ${b.boxes} Box${b.boxes > 1 ? 'es' : ''}`, flavour: flavour.id, price: (unit || 0) * b.boxes, qty: 1, boxes: b.boxes, type: 'bundle', color: flavour.primary, rgb: flavour.rgb, wooItems: [{ id: flavour.woo, quantity: b.boxes }] });
    }
  };

  return (
    <section className="py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-5">
        <Reveal className="text-center mb-14">
          <h2 className="font-display text-4xl md:text-6xl">CHOOSE YOUR <span className="text-flavour flavour-transition">S-MAN.</span></h2>
          <p className="text-white/50 mt-3">Selected flavour: <span style={{ color: flavour.primary }}>{flavour.name}</span> · change it anytime above.</p>
          {!available && (
            <p data-testid="flavour-coming-soon" className="mt-4 inline-block px-4 py-2 rounded-full border font-cond tracking-wide text-sm" style={{ borderColor: flavour.primary, color: flavour.primary }}>
              {flavour.name} — AVAILABLE FROM {flavour.comingSoon || 'SOON'}
            </p>
          )}
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {BUNDLES.filter((b) => b.id !== 'box-2').map((b, i) => {
            const popular = b.popular;
            const isMix = b.type === 'mix';
            const accent = isMix ? '#ff2d2d' : flavour.primary;
            const total = isMix ? mixSum : (unit || 0) * b.boxes;
            const per = isMix ? (availIds.length ? mixSum / availIds.length : 0) : unit;
            const disabled = !isMix && !available;
            return (
              <Reveal key={b.id} delay={i * 100}>
                <div className="relative rounded-3xl border bg-[#0b0b0b] p-7 h-full flex flex-col transition-all"
                  style={{ borderColor: popular ? accent : 'rgba(255,255,255,0.1)', boxShadow: popular ? `0 0 50px -15px rgba(${flavour.rgb},0.6)` : 'none', opacity: disabled ? 0.6 : 1 }}>
                  {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-cond tracking-wide text-black" style={{ background: accent }}>MOST POPULAR</span>}
                  <p className="font-cond tracking-wide text-white/50">{b.label}</p>
                  <h3 className="font-display text-3xl mt-1">{isMix ? `${availIds.length}-FLAVOUR MIX` : b.sub}</h3>
                  <div className="flex items-center justify-center my-6">
                    {isMix ? (
                      <div className="flex -space-x-4">{availIds.map((id) => (<img key={id} src={FLAVOURS[id].fruit} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-[#0b0b0b]" style={{ outline: `1px solid ${FLAVOURS[id].primary}` }} loading="lazy" />))}</div>
                    ) : (
                      <ProductBox flavourId={flavour.id} size="sm" scene />
                    )}
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className="font-display text-4xl" style={{ color: accent }}>{money(total)}</span>
                    </div>
                    <p className="text-xs text-white/40 mt-1">{money(per)} / box</p>
                  </div>
                  <button data-testid={`buy-${b.id}`} onClick={() => buy(b)} disabled={disabled} className="mt-6 w-full py-3.5 rounded-lg font-cond tracking-wide text-black hover:brightness-110 transition-all disabled:cursor-not-allowed" style={{ background: accent }}>
                    {disabled ? `FROM ${flavour.comingSoon || 'SOON'}` : 'ADD TO CART'}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FinalCta() {
  const navigate = useNavigate();
  const { order } = useFlavour();
  const { t } = useLang();
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[#020202]" />
      <div className="absolute inset-0 -z-10" style={{ background: 'radial-gradient(ellipse at 50% 120%, rgba(255,45,45,0.18), transparent 60%)' }} />
      <div className="max-w-[1400px] mx-auto px-5 w-full text-center">
        <Reveal><h2 className="font-display text-5xl md:text-8xl">SIX FLAVOURS.<br />ONE FORMULA.<br /><span className="text-[#ff2d2d]">ONE S-MAN.</span></h2></Reveal>
        <Reveal delay={200} className="flex items-end justify-center gap-2 md:gap-5 my-12">
          {order.map((id, i) => (
            <div key={id} style={{ transform: `translateY(${i % 2 ? 10 : 0}px)` }}><ProductBox flavourId={id} size="sm" scene /></div>
          ))}
        </Reveal>
        <Reveal delay={300} className="flex flex-col sm:flex-row gap-4 items-center justify-center">
          <button onClick={() => navigate('/flavours')} className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#ff2d2d] text-white font-cond tracking-wide text-lg hover:brightness-110 transition-all">{t('chooseFlavour')} ⚡</button>
          <button onClick={() => navigate('/flavours')} className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border border-white/20 text-white font-cond tracking-wide text-lg hover:bg-white/5 transition-all">{t('tryThemAll')} <ArrowRight className="w-5 h-5" /></button>
        </Reveal>
      </div>
    </section>
  );
}
